import { CheckCircle2, Clock, PowerOff, XCircle } from 'lucide-react';
import { Badge } from '@ncoder/ui';

interface StatusConfig {
  label: string;
  icon: typeof CheckCircle2;
  className: string;
}

/**
 * Status colours use the text-safe `-strong` variants.
 *
 * The base `--success` / `--warning` tokens are tuned as FILL colours; globals.css documents that
 * they measure ~2.1:1 and ~2.8:1 as *text* on their own 10% tint, well under WCAG AA. These badges
 * were using them exactly that way, so "Active" and "Pending" were the two lowest-contrast labels
 * on the page.
 */
const STATUS_CONFIG: Record<string, StatusConfig> = {
  active: {
    label: 'Active',
    icon: CheckCircle2,
    className: 'border-success/25 bg-success/10 text-success-strong',
  },
  accepted: {
    label: 'Accepted',
    icon: CheckCircle2,
    className: 'border-success/25 bg-success/10 text-success-strong',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    className: 'border-warning/25 bg-warning/10 text-warning-strong',
  },
  invited: {
    label: 'Invited',
    icon: Clock,
    className: 'border-warning/25 bg-warning/10 text-warning-strong',
  },
  deactivated: {
    label: 'Deactivated',
    icon: PowerOff,
    className: 'border-border/70 bg-muted/60 text-muted-foreground',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    className: 'border-border/70 bg-muted/60 text-muted-foreground',
  },
  expired: {
    label: 'Expired',
    icon: XCircle,
    className: 'border-destructive/25 bg-destructive/10 text-destructive-strong',
  },
};

const DEFAULT_CONFIG: StatusConfig = {
  label: '',
  icon: Clock,
  className: 'border-border/70 bg-muted/60 text-muted-foreground',
};

export function StatusBadge({ status }: { status: string }) {
  const key = status.toLowerCase();
  const cfg = STATUS_CONFIG[key] ?? { ...DEFAULT_CONFIG, label: status };

  return (
    <Badge
      variant="outline"
      className={`gap-1 whitespace-nowrap px-2 py-0 text-[11px] font-medium capitalize ${cfg.className}`}
    >
      <cfg.icon className="h-3 w-3 shrink-0" aria-hidden />
      {cfg.label}
    </Badge>
  );
}
